<template>
  <div class="honors-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 内容区域 -->
        <div v-else class="content-wrapper">
          <!-- 富文本内容（如果有） -->
          <div
            v-if="richtextHtml && richtextHtml.trim()"
            class="richtext-content"
          >
            <div v-html="richtextHtml"></div>

            <ResumeEmailDisplay />
          </div>

          <!-- 荣誉图片轮播 -->
          <div v-if="imageList && imageList.length > 0" class="honors-carousel-section">
            <h2 class="carousel-title">{{ t('honors.certificates') }}</h2>

            <!-- 轮播图容器 -->
            <div class="carousel-container">
              <div class="carousel-wrapper">
                <!-- 轮播图主区域 -->
                <div class="carousel-main">
                  <div
                    class="carousel-track"
                    :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
                  >
                    <div
                      v-for="(image, index) in imageList"
                      :key="index"
                      class="carousel-slide"
                    >
                      <div class="slide-content">
                        <div class="image-container">
                          <img
                            :src="image.path"
                            :alt="image.name"
                            class="honor-image"
                            @error="handleImageError"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 导航箭头 -->
                  <button
                    v-if="imageList.length > 1"
                    class="carousel-arrow prev-arrow"
                    @click="prevSlide"
                    :disabled="currentIndex === 0"
                  >
                    <el-icon size="24">
                      <ArrowLeft />
                    </el-icon>
                  </button>

                  <button
                    v-if="imageList.length > 1"
                    class="carousel-arrow next-arrow"
                    @click="nextSlide"
                    :disabled="currentIndex === imageList.length - 1"
                  >
                    <el-icon size="24">
                      <ArrowRight />
                    </el-icon>
                  </button>

                  <!-- 图片名称展示 -->
                  <div v-if="imageList[currentIndex]?.name" class="image-name">
                    {{ imageList[currentIndex].name }}
                  </div>
                </div>

                <!-- 缩略图导航 -->
                <div v-if="imageList.length > 1" class="thumbnail-nav">
                  <div
                    v-for="(image, index) in imageList"
                    :key="index"
                    class="thumbnail-item"
                    :class="{ active: currentIndex === index }"
                    @click="goToSlide(index)"
                  >
                    <div class="thumbnail-container">
                      <img
                        :src="image.path"
                        :alt="image.name"
                        class="thumbnail-image"
                      />
                      <div class="thumbnail-overlay"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning, ArrowLeft, ArrowRight, Trophy } from '@element-plus/icons-vue'
import { getRecruitmentIntro } from "@/assets/api/website";
import ResumeEmailDisplay from '@/views/components/ResumeEmailDisplay.vue'
// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const currentIndex = ref(0)
const autoPlayTimer = ref(null)
const isAutoPlaying = ref(true)

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.honors')
})

const richtextHtml = computed(() => {
  return pageData.value?.content?.richtext || ''
})

const imageList = computed(() => {
  return pageData.value?.content?.image_list || []
})

// 轮播图方法
const nextSlide = () => {
  if (currentIndex.value < imageList.value.length - 1) {
    currentIndex.value++
  } else {
    currentIndex.value = 0 // 循环到第一张
  }
  resetAutoPlay()
}

const prevSlide = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  } else {
    currentIndex.value = imageList.value.length - 1 // 循环到最后一张
  }
  resetAutoPlay()
}

const goToSlide = (index) => {
  currentIndex.value = index
  resetAutoPlay()
}

// 自动播放
const startAutoPlay = () => {
  if (imageList.value.length <= 1) return

  clearInterval(autoPlayTimer.value)
  autoPlayTimer.value = setInterval(() => {
    if (isAutoPlaying.value) {
      nextSlide()
    }
  }, 5000) // 5秒自动切换
}

const stopAutoPlay = () => {
  isAutoPlaying.value = false
  clearInterval(autoPlayTimer.value)
}

const resetAutoPlay = () => {
  stopAutoPlay()
  setTimeout(() => {
    isAutoPlaying.value = true
    startAutoPlay()
  }, 100)
}

// 图片加载失败处理
const handleImageError = (event) => {
  event.target.style.display = 'none'
  const placeholder = document.createElement('div')
  placeholder.className = 'image-error-placeholder'
  placeholder.innerHTML = `
    <div class="placeholder-icon">
      <el-icon size="40" color="var(--el-color-primary-light-6)">
        <Picture />
      </el-icon>
    </div>
    <p class="placeholder-text">图片加载失败</p>
  `
  event.target.parentElement.appendChild(placeholder)
}

// 获取页面数据
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // 使用当前的语言
    const currentLocale = locale.value

    const response = await getRecruitmentIntro({lang: currentLocale})

    if (response && response.content) {
      pageData.value = response

      // 如果有图片，启动自动播放
      if (imageList.value.length > 0) {
        nextTick(() => {
          startAutoPlay()
        })
      }
    } else {
      pageData.value = null
    }
  } catch (error) {
    console.error('获取页面数据失败:', error)
    errorMessage.value = t('common.load_failed')
  } finally {
    isLoading.value = false
  }
}

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchPageData()
})

// 组件挂载时获取数据
onMounted(() => {
  fetchPageData()
})

// 组件卸载时清理定时器
onUnmounted(() => {
  stopAutoPlay()
  clearInterval(autoPlayTimer.value)
})
</script>

<style scoped lang="scss">
@import '@/assets/css/carousel';
:deep(.richtext-content){
  h1, h2{
    letter-spacing: 2px !important;
    margin: 20px 0 15px !important;
  }
}
</style>